import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useMe } from '../context/MeContext.js';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

export function CoursePickerPage() {
  const { me, loading, error } = useMe();

  if (loading) return <PageState>Loading your courses…</PageState>;
  if (error) return <PageState tone="error">Error loading profile: {error}</PageState>;
  if (!me) return <PageState>No profile yet.</PageState>;

  if (me.courses.length === 0) {
    return (
      <div className="mx-auto mt-24 max-w-xl rounded-lg border border-border bg-bg-card p-10 text-center">
        <h1 className="font-display text-3xl font-bold tracking-tight text-fg">
          Welcome, {me.user.firstName ?? 'Superintendent'}.
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-fg-secondary">
          You aren&rsquo;t yet a member of any course. Ask your admin to invite you, or contact{' '}
          <a className="text-greeniq-400 hover:text-greeniq-300" href="mailto:nick@ageyetech.com">
            nick@ageyetech.com
          </a>{' '}
          to provision your course.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-14 md:px-10">
      <header className="mb-8 flex flex-col gap-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-greeniq-400">
          Signed in as {me.user.firstName ?? me.user.email ?? 'you'}
        </span>
        <h1 className="font-display text-4xl font-bold tracking-tight text-fg">Your courses</h1>
        <p className="max-w-xl text-sm text-fg-secondary">
          Pick a course to open its command center. Switch between courses at any time from the
          header.
        </p>
      </header>

      <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {me.courses.map((c) => (
          <li key={c.courseSlug}>
            <CourseCard
              slug={c.courseSlug}
              name={c.courseName}
              org={c.organizationName}
              role={c.role}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function CourseCard({
  slug,
  name,
  org,
  role,
}: {
  slug: string;
  name: string;
  org: string;
  role: string;
}) {
  return (
    <Link
      to={`/app/${slug}`}
      className={cn(
        'group relative flex h-full flex-col gap-4 overflow-hidden rounded-lg border border-border bg-bg-card p-5',
        'shadow-sm transition-all duration-[250ms] ease-out',
        'hover:-translate-y-0.5 hover:border-greeniq-500/30 hover:shadow-lg'
      )}
    >
      {/* Top accent bar — appears on hover */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-[2px] bg-greeniq-500 opacity-0 transition-opacity duration-[250ms] group-hover:opacity-100"
        style={{ boxShadow: '0 0 12px var(--green-glow)' }}
      />

      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="font-display text-lg font-semibold leading-tight text-fg">{name}</span>
          <span className="text-xs text-fg-secondary">{org}</span>
        </div>
        <Badge variant="default" className="shrink-0">
          {role.replace(/_/g, ' ')}
        </Badge>
      </div>

      <p className="mt-auto text-[13px] leading-relaxed text-fg-muted">
        Open the live briefing, map, and irrigation console for this course.
      </p>

      <div className="flex items-center justify-end">
        <Button
          asChild
          size="sm"
          variant="ghost"
          className="text-greeniq-400 group-hover:text-greeniq-300"
        >
          <span className="pointer-events-none">
            Open <ArrowRight size={14} />
          </span>
        </Button>
      </div>
    </Link>
  );
}

function PageState({
  children,
  tone = 'info',
}: {
  children: React.ReactNode;
  tone?: 'info' | 'error';
}) {
  return (
    <div
      className={cn(
        'mx-auto mt-24 max-w-xl rounded-lg border p-8 text-center text-sm',
        tone === 'error'
          ? 'border-[color:var(--color-error)]/30 bg-error-muted text-fg'
          : 'border-border bg-bg-card text-fg-secondary'
      )}
    >
      {children}
    </div>
  );
}
